import React, { useState } from 'react';
import Seo from '@/components/seo/Seo';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FaSpinner, FaMoneyBillWave, FaArrowLeft } from 'react-icons/fa';
import { toast } from 'sonner';
import { createOrder } from '@/api/orders';
import { useCart } from '@/context/CartContext';
import { formatPrice } from '@/lib/format';
import { EmptyState } from '@/modules/shop/components/ShopStates';

/** Pravila preslikavaju `CustomerRequest` na backendu — ista poruka na oba mesta. */
const checkoutSchema = z.object({
  fullName: z.string().trim().min(2, 'Ime i prezime su obavezni.').max(200, 'Ime je predugačko.'),
  phone: z
    .string()
    .trim()
    .regex(/^[+]?[-\d\s]{8,25}$/, 'Telefon nije u validnom formatu.'),
  email: z.string().trim().email('Email nije validan.').max(200, 'Email je predugačak.'),
  address: z.string().trim().min(1, 'Adresa je obavezna.').max(300, 'Adresa je predugačka.'),
  city: z.string().trim().min(1, 'Grad je obavezan.').max(120, 'Naziv grada je predugačak.'),
  postalCode: z.string().trim().regex(/^\d{4,20}$/, 'Poštanski broj nije validan.'),
  note: z.string().trim().max(1000, 'Napomena je predugačka.').optional().or(z.literal('')),
});

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items, total } = useCart();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(checkoutSchema) });

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      const order = await createOrder({
        customer: { ...values, note: values.note || null },
        items: items.map(({ productId, variantId, quantity }) => ({ productId, variantId, quantity })),
      });

      // Korpa se prazni tek na stranici uspeha — da se ne izgubi ako navigacija pukne.
      navigate(`/porudzbina/${order.orderNumber}?token=${encodeURIComponent(order.publicToken)}`, {
        state: { order },
        replace: true,
      });
    } catch (error) {
      const message = error?.response?.data?.message
        || 'Porudžbinu trenutno nije moguće poslati. Pokušajte ponovo za koji minut.';
      toast.error(message);
      setSubmitting(false);
    }
  };

  const inputClasses = (field) =>
    `w-full bg-white border ${
      errors[field] ? 'border-error' : 'border-neutral-200'
    } rounded-sm px-4 py-3 text-brand-primary placeholder:text-neutral-500
     focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-brand-accent transition-all duration-300`;

  if (items.length === 0) {
    return (
      <div className="pt-32 pb-20 min-h-[60vh]">
        <Seo title="Poručivanje" noindex />
        <div className="container-custom">
          <EmptyState
            title="Korpa je prazna"
            description="Dodajte proizvode pre nego što nastavite na poručivanje."
            actionTo="/shop"
            actionLabel="Idite u prodavnicu"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <Seo title="Poručivanje" noindex />

      <div className="pt-32 pb-20" data-testid="checkout-page">
        <div className="container-custom">
          <Link
            to="/korpa"
            className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-brand-accent transition-colors mb-8"
          >
            <FaArrowLeft size={12} />
            Nazad u korpu
          </Link>

          <h1 className="font-heading text-4xl text-brand-primary mb-10">Poručivanje</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="lg:col-span-2 space-y-6"
              data-testid="checkout-form"
              noValidate
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label htmlFor="fullName" className="block text-sm font-medium text-brand-primary mb-2">
                    Ime i prezime *
                  </label>
                  <input id="fullName" type="text" className={inputClasses('fullName')}
                    placeholder="Marko Marković" disabled={submitting} {...register('fullName')} />
                  {errors.fullName && <p className="text-error text-sm mt-2">{errors.fullName.message}</p>}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-brand-primary mb-2">
                    Telefon *
                  </label>
                  <input id="phone" type="tel" className={inputClasses('phone')}
                    placeholder="064 123 4567" disabled={submitting} {...register('phone')} />
                  {errors.phone && <p className="text-error text-sm mt-2">{errors.phone.message}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-brand-primary mb-2">
                    Email *
                  </label>
                  <input id="email" type="email" className={inputClasses('email')}
                    placeholder="vas@email.rs" disabled={submitting} {...register('email')} />
                  {errors.email && <p className="text-error text-sm mt-2">{errors.email.message}</p>}
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="address" className="block text-sm font-medium text-brand-primary mb-2">
                    Adresa *
                  </label>
                  <input id="address" type="text" className={inputClasses('address')}
                    placeholder="Ulica i broj" disabled={submitting} {...register('address')} />
                  {errors.address && <p className="text-error text-sm mt-2">{errors.address.message}</p>}
                </div>

                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-brand-primary mb-2">
                    Grad *
                  </label>
                  <input id="city" type="text" className={inputClasses('city')}
                    placeholder="Užice" disabled={submitting} {...register('city')} />
                  {errors.city && <p className="text-error text-sm mt-2">{errors.city.message}</p>}
                </div>

                <div>
                  <label htmlFor="postalCode" className="block text-sm font-medium text-brand-primary mb-2">
                    Poštanski broj *
                  </label>
                  <input id="postalCode" type="text" inputMode="numeric" className={inputClasses('postalCode')}
                    placeholder="31000" disabled={submitting} {...register('postalCode')} />
                  {errors.postalCode && <p className="text-error text-sm mt-2">{errors.postalCode.message}</p>}
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="note" className="block text-sm font-medium text-brand-primary mb-2">
                    Napomena
                  </label>
                  <textarea id="note" rows={4} className={inputClasses('note')}
                    placeholder="Npr. pozvati pre dostave" disabled={submitting} {...register('note')} />
                  {errors.note && <p className="text-error text-sm mt-2">{errors.note.message}</p>}
                </div>
              </div>

              {/* Način plaćanja — MVP ima samo pouzeće */}
              <div className="border border-brand-accent bg-brand-bgSoft p-5 rounded-sm flex items-start gap-4">
                <FaMoneyBillWave className="text-brand-accent mt-1" size={20} />
                <div>
                  <p className="font-medium text-brand-primary">Plaćanje pouzećem</p>
                  <p className="text-sm text-neutral-700">
                    Iznos plaćate kuriru pri preuzimanju paketa. Online plaćanje karticom trenutno nije dostupno.
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn-accent w-full flex items-center justify-center gap-3 disabled:opacity-60"
                data-testid="submit-order"
              >
                {submitting && <FaSpinner className="animate-spin" />}
                {submitting ? 'Slanje porudžbine…' : 'Pošalji porudžbinu'}
              </button>
            </form>

            {/* Rekapitulacija */}
            <div className="lg:col-span-1">
              <div className="bg-neutral-50 p-8 sticky top-28">
                <h2 className="font-heading text-xl text-brand-primary mb-6">Vaša porudžbina</h2>

                <div className="space-y-4 mb-6">
                  {items.map((item) => (
                    <div
                      key={`${item.productId}-${item.variantId ?? 'base'}`}
                      className="flex justify-between text-sm gap-4"
                    >
                      <span className="text-neutral-700">
                        {item.name} <span className="text-neutral-500">× {item.quantity}</span>
                      </span>
                      <span className="text-brand-primary whitespace-nowrap">
                        {formatPrice(item.price * item.quantity)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-neutral-200 pt-4 flex justify-between font-medium text-brand-primary">
                  <span>Međuzbir</span>
                  <span>{formatPrice(total)}</span>
                </div>
                <p className="text-xs text-neutral-500 mt-3">
                  Konačan iznos sa dostavom potvrđuje se u koraku posle slanja — cene se uvek računaju na serveru.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutPage;
